import { useState, useEffect, useRef } from "react"
import { useParams, Link } from "@tanstack/react-router"
import { useStartAttempt, useSaveResponse, useSubmitAttempt } from "@/lib/queries"
import { ApiError } from "@/lib/api"
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardDescription,
} from "@/components/ui/card"
import { Button } from "@/components/ui/button"
import { BookOpen, Loader2 } from "lucide-react"
import { FeedbackForm } from "@/components/training/FeedbackForm"
import { ClinicalReviewDrawer } from "@/components/training/ClinicalReviewDrawer"
import { FullNameDialog } from "@/components/FullNameDialog"
import { useAuth } from "@/contexts/AuthContext"
import type { StartedAttempt, SubmitResult } from "@/lib/types"

export function TakeQuizPage() {
  const { assignmentId } = useParams({ strict: false })
  const assignmentIdNum = assignmentId ? Number(assignmentId) : undefined

  // A passing attempt produces a certificate of completion, and one naming a
  // login handle is not usable as an accreditation record. So the name is
  // collected before the attempt exists at all — not at submission, where
  // refusing would mean discarding answers the person has already given.
  const { user, isLoading: userLoading } = useAuth()
  const needsFullName = !userLoading && !!user && !user.full_name

  const startAttempt = useStartAttempt()
  const saveResponse = useSaveResponse()
  const submitAttempt = useSubmitAttempt()

  const [attempt, setAttempt] = useState<StartedAttempt | null>(null)
  const [startError, setStartError] = useState<string | null>(null)
  const [selections, setSelections] = useState<Record<number, number>>({})
  // Per-question save status, so one question's failure can never be masked
  // by another question's success (or vice versa).
  const [saveStatus, setSaveStatus] = useState<Record<number, "saving" | "saved" | "error">>({})
  const [result, setResult] = useState<SubmitResult | null>(null)
  const [reviewOpen, setReviewOpen] = useState(false)

  // Start or resume exactly once per mount. The server decides which.
  const startedRef = useRef(false)
  useEffect(() => {
    if (!assignmentIdNum || startedRef.current) return
    // Hold until the name is on file. Nothing is created server-side in the
    // meantime, so backing out of the prompt leaves no half-started attempt.
    if (userLoading || needsFullName) return
    startedRef.current = true
    startAttempt.mutate(assignmentIdNum, {
      onSuccess: (started) => {
        setAttempt(started)
        setSelections(
          Object.fromEntries(
            started.responses.map((r) => [r.question_id, r.answer_option_id])
          )
        )
      },
      onError: (err) => {
        setStartError(
          err instanceof ApiError ? err.message : "An unexpected error occurred."
        )
      },
    })
  }, [assignmentIdNum, startAttempt, userLoading, needsFullName])

  const handleSelect = (questionId: number, optionId: number) => {
    if (!attempt) return
    setSelections((prev) => ({ ...prev, [questionId]: optionId }))
    setSaveStatus((prev) => ({ ...prev, [questionId]: "saving" }))
    saveResponse.mutate(
      {
        attempt_id: attempt.id,
        question_id: questionId,
        answer_option_id: optionId,
      },
      {
        onSuccess: () =>
          setSaveStatus((prev) => ({ ...prev, [questionId]: "saved" })),
        onError: () =>
          setSaveStatus((prev) => ({ ...prev, [questionId]: "error" })),
      }
    )
  }

  const handleSubmit = () => {
    if (!attempt) return
    submitAttempt.mutate(attempt.id, {
      onSuccess: (submitted) => setResult(submitted),
    })
  }

  if (needsFullName) {
    return <FullNameDialog open required />
  }

  if (startAttempt.isPending && !attempt) {
    return (
      <div className="flex items-center justify-center p-12">
        <Loader2 className="mr-2 h-5 w-5 animate-spin text-muted-foreground" />
        <span className="text-muted-foreground">Loading assessment…</span>
      </div>
    )
  }

  if (startError) {
    return (
      <div className="flex justify-center p-8">
        <Card className="w-full max-w-md">
          <CardHeader>
            <CardTitle>Unable to Start Assessment</CardTitle>
            <CardDescription>{startError}</CardDescription>
          </CardHeader>
          <CardContent>
            <Button asChild variant="outline">
              <Link to="/training">Back to My Training</Link>
            </Button>
          </CardContent>
        </Card>
      </div>
    )
  }

  if (result) {
    return (
      <div className="flex justify-center p-8">
        <div className="w-full max-w-md space-y-0">
          <Card>
            <CardHeader>
              <CardTitle>{result.passed ? "Passed" : "Not Passed"}</CardTitle>
              <CardDescription>
                {result.passed
                  ? "Congratulations — your assessment has been submitted."
                  : "Your assessment has been submitted. You can retake it from My Training."}
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <p className="text-sm">
                Your score: <span className="text-2xl font-bold">{result.score}%</span>
              </p>
              <Button asChild variant="outline">
                <Link to="/training">Back to My Training</Link>
              </Button>
            </CardContent>
          </Card>
          {assignmentIdNum && <FeedbackForm assignmentId={assignmentIdNum} />}
        </div>
      </div>
    )
  }

  if (!attempt) return null

  const questions = [...attempt.questions].sort((a, b) => a.position - b.position)
  const allAnswered = questions.every((q) => selections[q.id] !== undefined)

  // Page-level indicator derived from the per-question map, with failure
  // taking precedence over success: never show "Answers saved" while any
  // question's save is outstanding or failed.
  const saveStatusValues = Object.values(saveStatus)
  const isSaving = saveStatusValues.includes("saving")
  const hasSaveError = saveStatusValues.includes("error")
  const hasSavedAny = saveStatusValues.includes("saved")

  return (
    <div className="flex h-full overflow-hidden">
      <div className="flex-1 space-y-6 overflow-auto p-6">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">{attempt.quiz_title}</h1>
          <p className="text-sm text-muted-foreground">
            Answer all questions, then submit. Your answers save as you go.
          </p>
          {attempt.open_book.available && (
            <div className="mt-2 flex flex-wrap items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setReviewOpen(!reviewOpen)}
                aria-expanded={reviewOpen}
              >
                <BookOpen className="mr-2 h-4 w-4" />
                {reviewOpen ? "Hide" : "Open"} {attempt.open_book.clinical_review_title}
              </Button>
              <span className="text-sm text-muted-foreground">
                Open book — you may consult it while answering.
              </span>
            </div>
          )}
        </div>

        {attempt.outcome === "restarted" && (
          <div className="rounded-md border border-amber-300 bg-amber-50 px-4 py-3 text-sm text-amber-900 dark:border-amber-900/50 dark:bg-amber-900/20 dark:text-amber-200">
            This assessment was updated since you last opened it. Your earlier
            answers no longer apply, so you are starting again on the current
            version ({attempt.version_label}).
          </div>
        )}

        <div className="space-y-4">
          {questions.map((question, index) => {
            const sortedOptions = [...question.answer_options].sort(
              (a, b) => a.position - b.position
            )
            const questionSaveFailed = saveStatus[question.id] === "error"
            return (
              <Card
                key={question.id}
                className={questionSaveFailed ? "border-destructive" : undefined}
              >
                <CardHeader className="pb-3">
                  <CardTitle className="text-base font-medium">
                    {index + 1}. {question.prompt}
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <fieldset>
                    <legend className="sr-only">{question.prompt}</legend>
                    <div className="space-y-2">
                      {sortedOptions.map((option) => {
                        const inputId = `q${question.id}-o${option.id}`
                        const isSelected = selections[question.id] === option.id
                        return (
                          <label
                            key={option.id}
                            htmlFor={inputId}
                            className={[
                              "flex cursor-pointer items-center gap-3 rounded-md border px-4 py-3 text-sm transition-colors",
                              isSelected
                                ? "border-primary bg-primary/5 font-medium"
                                : "border-border hover:bg-muted/50",
                            ].join(" ")}
                          >
                            <input
                              id={inputId}
                              type="radio"
                              name={`question-${question.id}`}
                              value={option.id}
                              checked={isSelected}
                              onChange={() => handleSelect(question.id, option.id)}
                              className="accent-primary"
                            />
                            {option.text}
                          </label>
                        )
                      })}
                    </div>
                  </fieldset>
                  {questionSaveFailed && (
                    <p className="mt-2 text-sm text-destructive">
                      This answer did not save. Reselect it to try again.
                    </p>
                  )}
                </CardContent>
              </Card>
            )
          })}
        </div>

        <div className="flex items-center gap-3 pb-6">
          <Button
            onClick={handleSubmit}
            disabled={
              !allAnswered || isSaving || hasSaveError || submitAttempt.isPending
            }
          >
            {submitAttempt.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            {submitAttempt.isPending ? "Submitting…" : "Submit Assessment"}
          </Button>
          {!allAnswered && (
            <p className="text-sm text-muted-foreground">
              Answer all questions to submit.
            </p>
          )}
          <div role="status" aria-live="polite">
            {isSaving && (
              <p className="text-sm text-muted-foreground">Saving…</p>
            )}
            {!isSaving && hasSaveError && (
              <p className="text-sm text-destructive">
                An answer did not save. Check your connection and reselect it.
              </p>
            )}
            {!isSaving && !hasSaveError && hasSavedAny && (
              <p className="text-sm text-muted-foreground">Answers saved</p>
            )}
          </div>
          {submitAttempt.isError && (
            <p className="text-sm text-destructive">
              {submitAttempt.error instanceof ApiError
                ? submitAttempt.error.message
                : "Failed to submit. Please try again."}
            </p>
          )}
        </div>
      </div>
      {attempt.open_book.available && (
        <ClinicalReviewDrawer
          slug={attempt.open_book.clinical_review_slug!}
          title={attempt.open_book.clinical_review_title!}
          open={reviewOpen}
          onOpenChange={setReviewOpen}
        />
      )}
    </div>
  )
}
